// Tiles are locked to a fixed aspect ratio, so the column count that actually
// maximizes tile size depends on the container's real width/height at a given
// participant count - not a fixed rule ("4 -> 2 columns" breaks as soon as the
// window is resized narrower/wider, or the count is 5, 6, 7...). This
// brute-forces every column count from 1..n (n is always small - camera grids
// don't scale past a handful of people) and picks whichever yields the largest
// aspect-ratio-constrained tile.
//
// Shared by the group-room camera grid (VideoGridOverlay) and the meeting
// stage (MeetingStage), which have identical layout needs and would otherwise
// drift apart.
export const TILE_ASPECT_RATIO = 16 / 9;
export const GRID_GAP = 12;

// Returns the tile's own pixel width, not just a column count - a grid item
// under `repeat(cols, 1fr)` stretches to the full column width and derives its
// height from that via aspect-ratio, ignoring the row-height budget this
// function reasons about. Sizing columns to this exact tileWidth (instead of
// 1fr) is what keeps the height-bound cases from silently overflowing.
export function getOptimalTileSize(count, width, height, gap = GRID_GAP) {
  if (count <= 0 || width <= 0 || height <= 0) return null;
  let best = null;
  for (let cols = 1; cols <= count; cols++) {
    const rows = Math.ceil(count / cols);
    const cellWidth = (width - gap * (cols - 1)) / cols;
    const cellHeight = (height - gap * (rows - 1)) / rows;
    if (cellWidth <= 0 || cellHeight <= 0) continue;
    let tileWidth = cellWidth;
    let tileHeight = tileWidth / TILE_ASPECT_RATIO;
    if (tileHeight > cellHeight) {
      tileHeight = cellHeight;
      tileWidth = tileHeight * TILE_ASPECT_RATIO;
    }
    const area = tileWidth * tileHeight;
    if (!best || area > best.area) {
      best = { cols, tileWidth, area };
    }
  }
  return best;
}
